import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useCustomers } from '../api/hooks'
import { useAuthStore } from '../stores/auth'
import { initials } from '../lib/format'

/**
 * Header customer chip. A principal with ONE grant gets a plain chip; with
 * several it becomes a dropdown that re-scopes the console: selectCustomer
 * mints a fresh customer-scoped token, then the whole query cache is
 * invalidated so every page reloads in the new scope (query keys already
 * carry the customer uid — api/hooks.ts useScope).
 */
export function CustomerSwitcher() {
  const customer = useAuthStore((s) => s.customer)
  const selectCustomer = useAuthStore((s) => s.selectCustomer)
  const queryClient = useQueryClient()
  const customers = useCustomers()

  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const grants = customers.data ?? []
  // Until the grant list resolves (or if it fails) the chip stays static —
  // never flash a dropdown affordance we can't back.
  const switchable = grants.length > 1

  const chipContent = (
    <>
      <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-ostrel-navy text-[11px] font-semibold text-ostrel-gold">
        {initials(customer?.name ?? '?')}
      </span>
      <span className="min-w-0 text-left">
        <span className="block text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Customer
        </span>
        <span className="block truncate text-sm font-medium text-ostrel-ink">{customer?.name}</span>
      </span>
    </>
  )

  if (!switchable) {
    return (
      <div className="flex items-center gap-2.5 rounded-sm border border-ostrel-line bg-ostrel-card px-2.5 py-1.5">
        {chipContent}
      </div>
    )
  }

  const switchTo = async (uid: string) => {
    if (uid === customer?.unique_id) {
      setOpen(false)
      return
    }
    setSwitching(true)
    setError(null)
    try {
      await selectCustomer(uid)
      await queryClient.invalidateQueries()
      // Detail UIDs belong to the OLD customer — land on the closest
      // generic page instead of a 404/stale view.
      const section = location.pathname.match(/^\/[a-z-]+/)?.[0] ?? '/'
      const isDetail = location.pathname !== section && location.pathname !== `${section}/`
      if (isDetail || location.pathname.includes('/mapping')) navigate(section, { replace: true })
      setOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : err instanceof Error ? err.message : String(err))
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setError(null)
          setOpen((v) => !v)
        }}
        disabled={switching}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch customer"
        className="flex items-center gap-2.5 rounded-sm border border-ostrel-line bg-ostrel-card px-2.5 py-1.5 transition-colors hover:border-ostrel-ink-muted focus:outline-none focus:ring-1 focus:ring-ostrel-gold disabled:opacity-60"
      >
        {chipContent}
        <span className={`pl-1 text-xs text-ostrel-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Customers"
          className="absolute top-full left-0 z-20 mt-1 w-64 overflow-hidden rounded-sm border border-ostrel-line bg-ostrel-card shadow-xl"
        >
          <p className="border-b border-ostrel-line px-3 pt-2.5 pb-2 text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Switch customer
          </p>
          <ul className="max-h-72 overflow-y-auto py-1">
            {grants.map((grant) => {
              const active = grant.unique_id === customer?.unique_id
              return (
                <li key={grant.unique_id}>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={switching}
                    onClick={() => void switchTo(grant.unique_id)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors focus:outline-none focus:ring-1 focus:ring-ostrel-gold disabled:opacity-60 ${
                      active ? 'bg-ostrel-gold/10' : 'hover:bg-ostrel-light'
                    }`}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-ostrel-navy text-[10px] font-semibold text-ostrel-gold">
                      {initials(grant.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ostrel-ink">{grant.name}</span>
                      <span className="block text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                        {grant.role}
                      </span>
                    </span>
                    {active && <span className="text-xs text-ostrel-gold">●</span>}
                  </button>
                </li>
              )
            })}
          </ul>
          {error && <p className="border-t border-ostrel-line px-3 py-2 text-xs text-ostrel-danger">{error}</p>}
        </div>
      )}
    </div>
  )
}
